/**
 * Optimistic UI Observability Logger
 *
 * Logs mutation lifecycle, sync events, auth events, and invariant violations.
 * In development: console with grouping
 * In production: batched telemetry (stubbed for now, easy to add Sentry/PostHog)
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface MutationCreatedData {
  key: string;
  mutationId: string;
  action: 'add' | 'remove' | 'set';
  timestamp: number;
}

interface MutationSupersededData {
  key: string;
  oldMutationId: string;
  newMutationId: string;
}

interface MutationConfirmedData {
  key: string;
  mutationId: string;
  latencyMs: number;
  serverDuplicate?: boolean;
}

interface MutationFailedData {
  key: string;
  mutationId: string;
  error: string;
  statusCode?: number;
  willRollback: boolean;
}

interface MutationRolledbackData {
  key: string;
  mutationId: string;
  snapshotAgeMs: number;
}

interface MutationStaleExpiredData {
  key: string;
  mutationId: string;
  ageMs: number;
}

interface MutationDroppedData {
  key: string;
  mutationId: string;
  reason: 'logout' | 'auth_expired' | 'hard_rehydrate' | 'snapshot_expired';
}

interface SyncStartedData {
  trigger: 'focus' | 'interval' | 'online' | 'manual' | 'mutation_timeout';
}

interface SyncCompletedData {
  added: number;
  removed: number;
  protected: number;
  durationMs: number;
}

interface SyncFailedData {
  error: string;
  statusCode?: number;
  willRetry: boolean;
}

interface SyncStaleIgnoredData {
  serverSyncedAt: number;
  clientLastMutation: number;
  deltaMs: number;
}

interface SyncHardRehydrateData {
  reason: 'tab_inactive' | 'mobile_background' | 'user_switched' | 'max_divergence';
  inactiveMs?: number;
  mutationAgeMs?: number;
}

interface HydrationStartedData {
  userId: string;
}

interface HydrationCompletedData {
  itemCounts: {
    watched: number;
    watchlist: number;
    favorites: number;
    ratings: number;
    lists: number;
  };
  durationMs: number;
  paginated: boolean;
}

interface HydrationFailedData {
  error: string;
  statusCode?: number;
}

interface AuthUserSwitchedData {
  oldUserId: string | null;
  newUserId: string;
}

interface AuthUserMismatchData {
  stateUserId: string | null;
  authUserId: string | null;
}

interface InvariantViolationData {
  invariantId: string;
  expected: string;
  actual: string;
}

interface AlertData {
  type: 'repeated_rollbacks' | 'stuck_mutation' | 'high_conflict' | 'memory_pressure' | 'slow_hydration' | 'pending_overflow';
  details: Record<string, unknown>;
}

type LogEvent =
  | { type: 'mutation:created'; data: MutationCreatedData }
  | { type: 'mutation:superseded'; data: MutationSupersededData }
  | { type: 'mutation:confirmed'; data: MutationConfirmedData }
  | { type: 'mutation:failed'; data: MutationFailedData }
  | { type: 'mutation:rolledback'; data: MutationRolledbackData }
  | { type: 'mutation:stale_expired'; data: MutationStaleExpiredData }
  | { type: 'mutation:dropped'; data: MutationDroppedData }
  | { type: 'sync:started'; data: SyncStartedData }
  | { type: 'sync:completed'; data: SyncCompletedData }
  | { type: 'sync:failed'; data: SyncFailedData }
  | { type: 'sync:stale_ignored'; data: SyncStaleIgnoredData }
  | { type: 'sync:hard_rehydrate'; data: SyncHardRehydrateData }
  | { type: 'hydration:started'; data: HydrationStartedData }
  | { type: 'hydration:completed'; data: HydrationCompletedData }
  | { type: 'hydration:failed'; data: HydrationFailedData }
  | { type: 'auth:user_switched'; data: AuthUserSwitchedData }
  | { type: 'auth:user_mismatch'; data: AuthUserMismatchData }
  | { type: 'auth:token_expired'; data: Record<string, never> }
  | { type: 'invariant:violation'; data: InvariantViolationData }
  | { type: 'alert'; data: AlertData };

const LOG_LEVELS: Record<string, LogLevel> = {
  'mutation': 'debug',
  'sync': 'info',
  'hydration': 'info',
  'auth': 'info',
  'invariant': 'error',
  'alert': 'warn',
};

const isDev = import.meta.env.DEV;

// Production sampling rates
const SAMPLING_RATES: Record<string, number> = {
  'mutation': 0.01,    // 1% - high volume
  'sync': 0.10,        // 10%
  'hydration': 0.10,   // 10%
  'auth': 0.10,        // 10%
  'invariant': 1.0,    // 100% - always log
  'alert': 1.0,        // 100% - always log
};

function shouldSample(eventType: string): boolean {
  if (isDev) return true;

  const category = eventType.split(':')[0];
  const rate = SAMPLING_RATES[category] ?? 0.10;
  return Math.random() < rate;
}

function getLogLevel(eventType: string): LogLevel {
  const category = eventType.split(':')[0];
  return LOG_LEVELS[category] ?? 'info';
}

function formatForConsole(event: LogEvent): void {
  const level = getLogLevel(event.type);
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${event.type}]`;

  switch (level) {
    case 'debug':
      console.debug(prefix, event.data);
      break;
    case 'info':
      console.info(prefix, event.data);
      break;
    case 'warn':
      console.warn(prefix, event.data);
      break;
    case 'error':
      console.error(prefix, event.data);
      break;
  }
}

// Batched telemetry queue for production (stubbed)
const telemetryQueue: LogEvent[] = [];
const BATCH_SIZE = 10;
const BATCH_INTERVAL_MS = 5000;
let batchTimeout: ReturnType<typeof setTimeout> | null = null;

function flushTelemetry(): void {
  if (telemetryQueue.length === 0) return;

  // Stub: In production, send to Sentry/PostHog/custom endpoint
  // For now, just clear the queue
  // Example: fetch('/api/telemetry', { method: 'POST', body: JSON.stringify(telemetryQueue) });

  telemetryQueue.length = 0;
}

function queueTelemetry(event: LogEvent): void {
  telemetryQueue.push(event);

  if (telemetryQueue.length >= BATCH_SIZE) {
    flushTelemetry();
  } else if (!batchTimeout) {
    batchTimeout = setTimeout(() => {
      flushTelemetry();
      batchTimeout = null;
    }, BATCH_INTERVAL_MS);
  }
}

/**
 * Main logging function - use this for all optimistic UI events
 */
export function logLibraryEvent<T extends LogEvent['type']>(
  type: T,
  data: Extract<LogEvent, { type: T }>['data']
): void {
  const event = { type, data } as LogEvent;

  if (!shouldSample(type)) return;

  if (isDev) {
    formatForConsole(event);
  } else {
    queueTelemetry(event);
  }
}

// Convenience functions for common events
export const libraryLog = {
  mutationCreated: (data: MutationCreatedData) =>
    logLibraryEvent('mutation:created', data),

  mutationSuperseded: (data: MutationSupersededData) =>
    logLibraryEvent('mutation:superseded', data),

  mutationConfirmed: (data: MutationConfirmedData) =>
    logLibraryEvent('mutation:confirmed', data),

  mutationFailed: (data: MutationFailedData) =>
    logLibraryEvent('mutation:failed', data),

  mutationRolledback: (data: MutationRolledbackData) =>
    logLibraryEvent('mutation:rolledback', data),

  mutationStaleExpired: (data: MutationStaleExpiredData) =>
    logLibraryEvent('mutation:stale_expired', data),

  mutationDropped: (data: MutationDroppedData) =>
    logLibraryEvent('mutation:dropped', data),

  syncStarted: (data: SyncStartedData) =>
    logLibraryEvent('sync:started', data),

  syncCompleted: (data: SyncCompletedData) =>
    logLibraryEvent('sync:completed', data),

  syncFailed: (data: SyncFailedData) =>
    logLibraryEvent('sync:failed', data),

  syncStaleIgnored: (data: SyncStaleIgnoredData) =>
    logLibraryEvent('sync:stale_ignored', data),

  syncHardRehydrate: (data: SyncHardRehydrateData) =>
    logLibraryEvent('sync:hard_rehydrate', data),

  hydrationStarted: (data: HydrationStartedData) =>
    logLibraryEvent('hydration:started', data),

  hydrationCompleted: (data: HydrationCompletedData) =>
    logLibraryEvent('hydration:completed', data),

  hydrationFailed: (data: HydrationFailedData) =>
    logLibraryEvent('hydration:failed', data),

  authUserSwitched: (data: AuthUserSwitchedData) =>
    logLibraryEvent('auth:user_switched', data),

  authUserMismatch: (data: AuthUserMismatchData) =>
    logLibraryEvent('auth:user_mismatch', data),

  authTokenExpired: () =>
    logLibraryEvent('auth:token_expired', {}),

  invariantViolation: (data: InvariantViolationData) =>
    logLibraryEvent('invariant:violation', data),

  alert: (data: AlertData) =>
    logLibraryEvent('alert', data),
};

export type { LogEvent, MutationCreatedData, MutationConfirmedData, MutationFailedData };
